/**
 * gestures.js — the drag physics shared by every swipeable surface.
 *
 * Extracted from card_swipe_controller.js and notification_swipe_controller.js,
 * which had each hand-rolled the same four things with different constants:
 * axis-lock, rubber-band damping, a threshold crossing, and a FLIP settle for
 * the rows left behind. sheet_controller.js is the third consumer.
 *
 * SSOT split, and it matters:
 *   - THIS module owns the MATHS. How resistance curves, what counts as a
 *     horizontal drag, how a FLIP is committed.
 *   - Each CONTROLLER owns its own TUNING. A notification row resists
 *     differently from a list card because they are different sizes doing
 *     different jobs; the constants stay at the call site and are passed in.
 *   - tokens/semantic.css owns the EASING and DURATION values. `motionToken()`
 *     reads them so a transition set from JS cannot drift from one set in CSS.
 *
 * Framework-free by design: no Stimulus import, no DOM assumptions beyond the
 * elements handed to it, so it is equally usable from a controller, a plain
 * script, or a test.
 */

/**
 * Decide whether a drag is horizontal or vertical, once, at the deadzone edge.
 *
 * Committing to an axis on the first meaningful movement is what lets a
 * vertical drag fall through to native scrolling instead of being swallowed as
 * a failed swipe. Returning null means "not yet" — the caller should keep
 * waiting rather than guess.
 *
 * @param {number} dx        horizontal travel since pointerdown
 * @param {number} dy        vertical travel since pointerdown
 * @param {number} deadzone  px of slop before an axis is chosen
 * @returns {'h'|'v'|null}
 */
export function lockAxis(dx, dy, deadzone) {
    if (Math.abs(dx) < deadzone && Math.abs(dy) < deadzone) return null;
    return Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
}

/**
 * Damp a drag so the element resists further the harder it is pulled.
 *
 * Used for travel that has nowhere to go: an unavailable swipe direction, or an
 * overscroll bump on a list that does not scroll. The element still tracks the
 * finger, so the surface feels alive rather than dead, but it visibly refuses.
 *
 * @param {number} delta       raw travel
 * @param {number} resistance  px at which the pull is halved; lower = stiffer
 * @param {number} scale       overall travel multiplier
 * @returns {number} damped travel
 */
export function rubberBand(delta, resistance, scale) {
    return (delta / (1 + Math.abs(delta) / resistance)) * scale;
}

/**
 * Has the drag passed the point where releasing would commit the action?
 *
 * Both a floor and a fraction, because neither alone works: a pure fraction
 * makes narrow rows trivially easy to fire, and a pure floor makes wide ones
 * feel like hard work.
 *
 * @param {number} delta     travel along the gesture axis
 * @param {number} minPx     absolute floor
 * @param {number} fraction  fraction of `width` that also qualifies
 * @param {number} width     the element's extent along the axis
 * @returns {boolean}
 */
export function crossedThreshold(delta, minPx, fraction, width) {
    return Math.abs(delta) > Math.max(minPx, width * fraction);
}

/**
 * One haptic pulse through the bridge facade.
 *
 * The facade (static/js/chippie-bridge.js) owns per-runtime transport: Strada on
 * iOS, window.ChippieNative on Android, navigator.vibrate in the PWA. Callers
 * never branch on platform, and a missing facade is silent — a gesture must not
 * throw because a device cannot buzz.
 *
 * Firing this ONCE per threshold crossing rather than per move event is the
 * caller's job; every consumer already tracks an armed flag for that reason.
 *
 * @param {string} [style]  'light' | 'medium' | 'heavy', per the facade's contract
 */
export function bump(style) {
    if (window.ChippieBridge && window.ChippieBridge.haptic) {
        try {
            window.ChippieBridge.haptic(style);
        } catch (_e) {
            /* facade gone mid-gesture; a missing buzz is not worth an exception */
        }
    }
}

/**
 * Read a motion token off the document root.
 *
 * Lets a JS-set transition use the same curve as the stylesheets, so the two
 * cannot drift. Cached because getComputedStyle is a forced style read and a
 * gesture may ask for the same curve on every release.
 *
 * Values are read lazily rather than at module load: the token stylesheets may
 * not have applied yet when the bundle is parsed.
 *
 * The cache is never invalidated, which is only safe because no theme file
 * overrides a --dur-* or --ease-* token — motion is defined once in
 * tokens/semantic.css and themes change colour only. That is a real constraint,
 * not an observation, so tests/unit/test_design_token_ratchet.py enforces it.
 *
 * @param {string} name  a token name, e.g. '--ease-settle' or '--dur-slow'
 * @returns {string} the resolved value, or '' if the token is not defined
 */
const _tokenCache = new Map();

export function motionToken(name) {
    if (_tokenCache.has(name)) return _tokenCache.get(name);
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim();
    if (value) _tokenCache.set(name, value);
    return value;
}

/**
 * FLIP the elements a removed row left behind, so the gap closes as a slide
 * rather than a jump.
 *
 * Each entry carries its own delta because the two call sites measure it
 * differently: a notification list knows every follower moved by exactly the
 * removed row's height, while an entity list measures each row's real
 * before/after rect. The maths of committing the inversion is the same either
 * way, and that is what lives here.
 *
 * Animates transform only — compositor work, no per-frame layout — and clears
 * the inline overrides afterwards so the elements are left as they were found.
 *
 * @param {Array<{el: HTMLElement, delta: number}>} entries  non-zero deltas only
 * @param {number} durationMs
 * @param {string} [easingValue]  a resolved easing; defaults to --ease-settle
 */
let _flipRun = 0;

export function flipSettle(entries, durationMs, easingValue) {
    const moving = entries.filter((entry) => entry.el && entry.delta);
    if (!moving.length) return;

    const ease = easingValue || motionToken('--ease-settle') || 'ease-out';

    // Invert: put each element back where it visually was a frame ago.
    moving.forEach(({ el, delta }) => {
        el.style.transition = 'none';
        el.style.transform = 'translateY(' + delta + 'px)';
    });

    // Commit those start offsets before transitioning away from them. Reading
    // offsetHeight forces the reflow; without it the browser coalesces both
    // writes and nothing animates.
    void moving[0].el.offsetHeight;

    // Stamp each element with the run that owns it. Two dismissals in quick
    // succession put the same element in two flips, and without this the FIRST
    // run's cleanup fires mid-way through the SECOND and snaps it to its end
    // position. Only the run still named on the element may clear it.
    const run = ++_flipRun;

    requestAnimationFrame(() => {
        moving.forEach(({ el }) => {
            el.dataset.flipRun = String(run);
            el.style.transition = 'transform ' + durationMs + 'ms ' + ease;
            el.style.transform = '';
        });
        setTimeout(() => {
            moving.forEach(({ el }) => {
                if (el.dataset.flipRun !== String(run)) return;
                delete el.dataset.flipRun;
                el.style.transition = '';
                el.style.transform = '';
            });
        }, durationMs + 40);
    });
}
